import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { usersAPI, documentsAPI } from '../utils/api';
import CameraCapture from '../components/CameraCapture';
import { useNavigate } from 'react-router-dom';

async function dlDoc(id, name) {
  try {
    const token = localStorage.getItem('piki_token');
    const res = await fetch(`/api/documents/${id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = name || 'document';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch { alert('Download failed — please try again.'); }
}

function SuspendModal({ user, onClose, onSuspend }) {
  const [reason, setReason] = useState('');
  if (!user) return null;
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center' }}
      onClick={onClose}>
      <div style={{ background:'white',borderRadius:14,padding:24,width:420,maxWidth:'95vw' }} onClick={e=>e.stopPropagation()}>
        <h3 style={{ fontSize:16,marginBottom:4 }}>⚠️ {user.suspended ? 'Reinstate' : 'Suspend'} Account</h3>
        <p style={{ fontSize:13,color:'var(--muted)',marginBottom:16 }}>{user.fullName} · {user.memberNumber||user.phone}</p>
        {!user.suspended && (
          <div className="form-group">
            <label className="form-label">Reason for suspension</label>
            <input className="form-input" placeholder="e.g. Suspected document fraud..." value={reason} onChange={e=>setReason(e.target.value)}/>
          </div>
        )}
        <div style={{ display:'flex',gap:8,marginTop:12 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className={`btn ${user.suspended?'btn-primary':'btn-danger'}`} onClick={()=>onSuspend(user, reason)}>
            {user.suspended ? '✅ Reinstate Account' : '🚫 Suspend Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OnboardReviewModal({ person, onClose, onApprove, onReject, onSuspend }) {
  const [docs, setDocs]   = useState([]);
  const [reason, setReason] = useState('');
  useEffect(()=>{
    if (person) documentsAPI.getForUser(person.id).then(r=>setDocs(r.data)).catch(()=>{});
  }, [person]);
  if (!person) return null;

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}
      onClick={onClose}>
      <div style={{ background:'white',borderRadius:14,width:'100%',maxWidth:640,maxHeight:'92vh',overflow:'hidden',display:'flex',flexDirection:'column' }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:800,fontSize:16 }}>
              {person.role === 'member' ? '👥' : '🏍️'} Review: {person.fullName}
            </div>
            <div style={{ fontSize:12,color:'var(--muted)' }}>
              {person.memberNumber||'No member no.'} · {person.phone} · KYC: <strong style={{color:'var(--yellow)'}}>{person.kycStatus}</strong>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'#F1F5F9',border:'none',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontWeight:600 }}>✕</button>
        </div>

        <div style={{ overflow:'auto',flex:1,padding:'16px 20px' }}>
          {/* Profile details */}
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16 }}>
            {[
              ['Role', person.role],
              ['National ID', person.nationalId],
              ['County', person.profile?.county],
              ['Registered', new Date(person.createdAt).toLocaleDateString('en-KE')],
              person.agentName && ['Agent', `${person.agentName} (${person.agentCode})`],
              person.profile?.bikeReg && ['Bike Reg', person.profile.bikeReg],
              person.profile?.licenseNumber && ['License', person.profile.licenseNumber],
              person.profile?.bikeType && ['Bike Type', person.profile.bikeType],
              person.profile?.isBikeOwner === false && ['Bike Owner', `${person.profile.ownerName||'?'} · ${person.profile.ownerPhone||'?'}`],
            ].filter(Boolean).map(([k,v])=>(
              <div key={k} style={{ background:'var(--bg)',borderRadius:7,padding:'8px 12px' }}>
                <div style={{ fontSize:10,color:'var(--muted)',fontWeight:600,textTransform:'uppercase',marginBottom:2 }}>{k}</div>
                <div style={{ fontSize:12,fontWeight:600 }}>{v||'—'}</div>
              </div>
            ))}
          </div>

          {/* KYC Documents */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13,fontWeight:700,marginBottom:8 }}>📎 KYC Documents ({docs.length})</div>
            {docs.length === 0 ? (
              <div style={{ background:'#FFF8E1',border:'1px solid #FDE68A',borderRadius:8,padding:'10px 12px',fontSize:12,color:'#92400E' }}>
                ⚠️ No KYC documents uploaded. Reject until documents are provided.
              </div>
            ) : (
              <div style={{ display:'flex',flexDirection:'column',gap:7 }}>
                {docs.map(d=>(
                  <div key={d.id} style={{ display:'flex',alignItems:'center',gap:10,background:'white',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px' }}>
                    <span style={{ fontSize:16 }}>{d.mimeType?.startsWith('image/')?'🖼️':'📄'}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12,fontWeight:600 }}>{d.originalName}</div>
                      <div style={{ fontSize:11,color:'var(--muted)' }}>{d.docLabel||d.docType}</div>
                    </div>
                    {d.verified && <span style={{ fontSize:10,background:'var(--green-light)',color:'var(--green)',padding:'1px 6px',borderRadius:4,fontWeight:700 }}>✓ Verified</span>}
                    <button onClick={()=>dlDoc(d.id, d.originalName)}
                      style={{ fontSize:12,background:'var(--green-light)',border:'none',borderRadius:6,padding:'4px 10px',cursor:'pointer',color:'var(--green)',fontWeight:600 }}>⬇️</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          {person.kycStatus === 'pending' && (
            <div style={{ borderTop:'1px solid var(--border)',paddingTop:14 }}>
              <div className="form-group">
                <label className="form-label">Rejection reason (if rejecting)</label>
                <input className="form-input" placeholder="e.g. Documents incomplete, ID not clear..." value={reason} onChange={e=>setReason(e.target.value)}/>
              </div>
              <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginTop:8 }}>
                <button className="btn btn-primary" onClick={()=>onApprove(person.id)}>✅ Approve KYC</button>
                <button className="btn btn-danger"  onClick={()=>onReject(person.id, reason)}>❌ Reject KYC</button>
                <button className="btn btn-secondary" onClick={()=>onSuspend(person)}>⚠️ Suspend Account</button>
              </div>
            </div>
          )}
          {person.kycStatus !== 'pending' && (
            <div style={{ display:'flex',gap:8,marginTop:8 }}>
              <button className="btn btn-secondary" onClick={()=>onSuspend(person)}>
                {person.suspended ? '✅ Reinstate Account' : '⚠️ Suspend Account'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Quick Create Agent Modal (embedded in Overview) ──────────────────────────
function QuickCreateAgent({ onClose, onDone }) {
  const [form,setForm]=useState({fullName:'',phone:'',email:'',nationalId:'',region:'',password:'',commission:''});
  const [creating,setCreating]=useState(false); const [err,setErr]=useState(''); const [ok,setOk]=useState('');
  const [tempId]=useState(()=>'AGT_'+Date.now());
  const [idDoc,setIdDoc]=useState(null); const [idUploading,setIdUploading]=useState(false);
  const COUNTIES=['Nairobi','Mombasa','Kisumu','Nakuru','Eldoret','Thika','Nyeri','Meru','Kitale','Kakamega','Bungoma','Machakos','Embu','Kericho','Kilifi','Kwale','Murang\'a','Kiambu','Migori','Kisii','Homa Bay','Siaya','Busia','Trans Nzoia','Uasin Gishu','Nandi','Baringo','West Pokot','Samburu','Turkana','Marsabit','Isiolo','Mwingi','Tana River','Lamu','Taita Taveta','Garissa','Wajir','Mandera','Narok','Kajiado','Laikipia','Nyahururu','Nyandarua','Kirinyaga','Tharaka Nithi','Vihiga','Butere'];
  const uploadId=(file)=>{
    if(!file) return;
    setIdUploading(true);
    const fd=new FormData(); fd.append('files',file); fd.append('docType','national_id'); fd.append('tempUploadId',tempId);
    fetch('/api/documents/upload-kyc',{method:'POST',body:fd}).then(r=>r.json()).then(d=>{if(d.documents?.[0]) setIdDoc(d.documents[0]);}).catch(()=>{}).finally(()=>setIdUploading(false));
  };
  const submit=async()=>{
    if(!form.fullName||!form.phone||!form.nationalId||!form.region||!form.password) return setErr('Full name, phone, national ID, region and password are required');
    if(form.password.length<8) return setErr('Password must be at least 8 characters');
    setCreating(true); setErr('');
    try { const res=await usersAPI.createAgent(form); const {agentCode,user}=res.data;
      if(idDoc&&user?.id) await fetch('/api/documents/attach-kyc',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('piki_token')},body:JSON.stringify({tempUploadId:tempId,userId:user.id})}).catch(()=>{});
      setOk(`✅ Agent created!\nCode: ${agentCode}\nPhone: ${form.phone}\nPass: ${form.password}`); if(onDone) onDone(); }
    catch(e){ setErr(e.response?.data?.error||'Failed'); } finally{setCreating(false);}
  };
  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(5,14,31,.75)',backdropFilter:'blur(5px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{width:'100%',maxWidth:460,background:'white',borderRadius:16,boxShadow:'0 32px 80px rgba(0,0,0,.4)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
        <div style={{background:'linear-gradient(135deg,#059669,#047857)',padding:'13px 17px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:9}}><div style={{width:30,height:30,borderRadius:7,background:'rgba(255,255,255,.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15}}>🤝</div><div><div style={{fontWeight:800,fontSize:13,color:'white'}}>Create Field Agent</div><div style={{fontSize:10,color:'rgba(255,255,255,.5)'}}>New agent account with unique code</div></div></div>
          <button onClick={onClose} style={{background:'rgba(255,255,255,.1)',border:'none',borderRadius:7,width:28,height:28,color:'white',cursor:'pointer',fontSize:15}}>✕</button>
        </div>
        <div style={{padding:'13px 16px 16px',maxHeight:'70vh',overflowY:'auto'}}>
          {err&&<div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:7,padding:'8px 11px',fontSize:12,color:'#991B1B',marginBottom:9}}>❌ {err}</div>}
          {ok?(<div><div style={{background:'#F0FDF4',border:'2px solid #A7F3D0',borderRadius:10,padding:'12px',marginBottom:9}}><div style={{fontSize:12,color:'#047857',fontFamily:'monospace',background:'#ECFDF5',borderRadius:5,padding:'8px',whiteSpace:'pre',lineHeight:1.9}}>{ok}</div></div><div style={{display:'flex',gap:7}}><button style={{flex:1,padding:'9px',background:'#059669',color:'white',border:'none',borderRadius:7,fontWeight:700,fontSize:12,cursor:'pointer'}} onClick={()=>{setOk('');setForm({fullName:'',phone:'',email:'',nationalId:'',region:'',password:'',commission:''});setIdDoc(null);}}>➕ Create Another</button><button style={{flex:1,padding:'9px',background:'#F3F4F6',color:'#374151',border:'none',borderRadius:7,fontWeight:700,fontSize:12,cursor:'pointer'}} onClick={onClose}>✓ Done</button></div></div>):(
          <div style={{display:'flex',flexDirection:'column',gap:7}}>
            {[{key:'fullName',label:'Full Name',ph:'e.g. John Kamau',type:'text',req:true},{key:'phone',label:'Phone',ph:'+254700000000',type:'tel',req:true},{key:'nationalId',label:'National ID',ph:'e.g. 12345678',type:'text',req:true},{key:'email',label:'Email (optional)',ph:'agent@email.com',type:'email',req:false},{key:'password',label:'Password',ph:'Min 8 chars',type:'password',req:true},{key:'commission',label:'Commission %',ph:'Default 5%',type:'number',req:false}].map(f=>(
              <div key={f.key}><label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:2}}>{f.label}{f.req&&<span style={{color:'#EF4444',marginLeft:2}}>*</span>}</label><input type={f.type} placeholder={f.ph} style={{width:'100%',border:'1.5px solid #E5E7EB',borderRadius:6,padding:'6px 9px',fontSize:12,outline:'none',boxSizing:'border-box',fontFamily:'inherit'}} value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} onFocus={e=>e.target.style.borderColor='#059669'} onBlur={e=>e.target.style.borderColor='#E5E7EB'}/></div>
            ))}
            <div><label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:2}}>Region / County<span style={{color:'#EF4444',marginLeft:2}}>*</span></label><select style={{width:'100%',border:'1.5px solid #E5E7EB',borderRadius:6,padding:'6px 9px',fontSize:12,outline:'none',boxSizing:'border-box',fontFamily:'inherit'}} value={form.region||''} onChange={e=>setForm(p=>({...p,region:e.target.value}))} onFocus={e=>e.target.style.borderColor='#059669'} onBlur={e=>e.target.style.borderColor='#E5E7EB'}><option value="">Select county…</option>{COUNTIES.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:4}}>📎 Copy of National ID <span style={{fontSize:10,color:'#9CA3AF'}}>(optional but recommended)</span></label>
              <div style={{border:`2px dashed ${idDoc?'#059669':'#D1D5DB'}`,borderRadius:8,padding:'9px 12px',textAlign:'center',background:idDoc?'#F0FDF4':'#FAFAFA'}}>
                {idDoc?(<div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}><span style={{fontSize:11,color:'#059669',fontWeight:700}}>✅ {idDoc.originalName}</span><button type="button" onClick={()=>setIdDoc(null)} style={{fontSize:10,color:'#EF4444',background:'none',border:'none',cursor:'pointer'}}>✕</button></div>):(
                  <div><div style={{fontSize:11,color:'#6B7280',marginBottom:5}}>{idUploading?'⏳ Uploading…':'📷 Photo or file of ID'}</div>
                    <input type="file" accept="image/*,application/pdf" style={{display:'none'}} id="qdAgentIdFile" onChange={e=>uploadId(e.target.files[0])}/>
                    <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                      <button type="button" disabled={idUploading} onClick={()=>document.getElementById('qdAgentIdFile')?.click()} style={{fontSize:11,padding:'4px 10px',background:'white',border:'1.5px solid #D1D5DB',borderRadius:6,cursor:'pointer',fontWeight:600}}>📁 Browse</button>
                      <CameraCapture disabled={idUploading} color="#059669" onCapture={f=>uploadId(f)}/>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button style={{width:'100%',marginTop:4,padding:'10px',background:'linear-gradient(135deg,#059669,#00D68F)',color:'white',border:'none',borderRadius:8,fontWeight:800,fontSize:13,cursor:'pointer'}} onClick={submit} disabled={creating}>{creating?'⏳ Creating…':'🤝 Create Agent'}</button>
          </div>)}
        </div>
      </div>
    </div>
  );
}

// ─── Quick Create Admin Modal ──────────────────────────────────────────────────
function QuickCreateAdmin({ onClose, onDone }) {
  const [form,setForm]=useState({fullName:'',phone:'',email:'',password:''});
  const [creating,setCreating]=useState(false); const [err,setErr]=useState(''); const [ok,setOk]=useState('');
  const [tempId]=useState(()=>'ADM_'+Date.now());
  const [idDoc,setIdDoc]=useState(null); const [idUploading,setIdUploading]=useState(false);
  const uploadId=(file)=>{
    if(!file) return;
    setIdUploading(true);
    const fd=new FormData(); fd.append('files',file); fd.append('docType','national_id'); fd.append('tempUploadId',tempId);
    fetch('/api/documents/upload-kyc',{method:'POST',body:fd}).then(r=>r.json()).then(d=>{if(d.documents?.[0]) setIdDoc(d.documents[0]);}).catch(()=>{}).finally(()=>setIdUploading(false));
  };
  const submit=async()=>{
    if(!form.fullName||!form.phone||!form.password) return setErr('Full name, phone and password are required');
    if(form.password.length<8) return setErr('Password must be at least 8 characters');
    setCreating(true); setErr('');
    try { const res=await usersAPI.createAdmin(form); const adminUser=res.data.user||res.data;
      if(idDoc&&adminUser?.id) await fetch('/api/documents/attach-kyc',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('piki_token')},body:JSON.stringify({tempUploadId:tempId,userId:adminUser.id})}).catch(()=>{});
      setOk(`✅ Admin created!\nPhone: ${form.phone}\nPass: ${form.password}`); if(onDone) onDone(); }
    catch(e){ setErr(e.response?.data?.error||'Failed'); } finally{setCreating(false);}
  };
  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(5,14,31,.75)',backdropFilter:'blur(5px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{width:'100%',maxWidth:440,background:'white',borderRadius:16,boxShadow:'0 32px 80px rgba(0,0,0,.4)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
        <div style={{background:'linear-gradient(135deg,#1E1B4B,#312E81)',padding:'13px 17px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:9}}><div style={{width:30,height:30,borderRadius:7,background:'rgba(165,180,252,.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15}}>🏢</div><div><div style={{fontWeight:800,fontSize:13,color:'white'}}>Create Admin Account</div><div style={{fontSize:10,color:'rgba(255,255,255,.4)'}}>Full system access — use carefully</div></div></div>
          <button onClick={onClose} style={{background:'rgba(255,255,255,.1)',border:'none',borderRadius:7,width:28,height:28,color:'white',cursor:'pointer',fontSize:15}}>✕</button>
        </div>
        <div style={{padding:'13px 16px 16px',maxHeight:'70vh',overflowY:'auto'}}>
          {err&&<div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:7,padding:'8px 11px',fontSize:12,color:'#991B1B',marginBottom:9}}>❌ {err}</div>}
          {ok?(<div><div style={{background:'#F0FDF4',border:'2px solid #A7F3D0',borderRadius:10,padding:'12px',marginBottom:9}}><div style={{fontSize:12,color:'#047857',fontFamily:'monospace',background:'#ECFDF5',borderRadius:5,padding:'8px',whiteSpace:'pre',lineHeight:1.9}}>{ok}</div></div><div style={{display:'flex',gap:7}}><button style={{flex:1,padding:'9px',background:'linear-gradient(135deg,#4F46E5,#7C3AED)',color:'white',border:'none',borderRadius:7,fontWeight:700,fontSize:12,cursor:'pointer'}} onClick={()=>{setOk('');setForm({fullName:'',phone:'',email:'',password:''});setIdDoc(null);}}>➕ Create Another</button><button style={{flex:1,padding:'9px',background:'#F3F4F6',color:'#374151',border:'none',borderRadius:7,fontWeight:700,fontSize:12,cursor:'pointer'}} onClick={onClose}>✓ Done</button></div></div>):(
          <div style={{display:'flex',flexDirection:'column',gap:7}}>
            {[{key:'fullName',label:'Full Name',ph:'e.g. Jane Wanjiku',type:'text',req:true},{key:'phone',label:'Phone',ph:'+254700000000',type:'tel',req:true},{key:'email',label:'Email (optional)',ph:'admin@email.com',type:'email',req:false},{key:'password',label:'Password',ph:'Min 8 chars',type:'password',req:true}].map(f=>(
              <div key={f.key}><label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:2}}>{f.label}{f.req&&<span style={{color:'#EF4444',marginLeft:2}}>*</span>}</label><input type={f.type} placeholder={f.ph} style={{width:'100%',border:'1.5px solid #E5E7EB',borderRadius:6,padding:'6px 9px',fontSize:12,outline:'none',boxSizing:'border-box',fontFamily:'inherit'}} value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} onFocus={e=>e.target.style.borderColor='#4F46E5'} onBlur={e=>e.target.style.borderColor='#E5E7EB'}/></div>
            ))}
            <div>
              <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:4}}>📎 Copy of National ID <span style={{fontSize:10,color:'#9CA3AF'}}>(optional but recommended)</span></label>
              <div style={{border:`2px dashed ${idDoc?'#4F46E5':'#D1D5DB'}`,borderRadius:8,padding:'9px 12px',textAlign:'center',background:idDoc?'#EEF2FF':'#FAFAFA'}}>
                {idDoc?(<div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}><span style={{fontSize:11,color:'#4F46E5',fontWeight:700}}>✅ {idDoc.originalName}</span><button type="button" onClick={()=>setIdDoc(null)} style={{fontSize:10,color:'#EF4444',background:'none',border:'none',cursor:'pointer'}}>✕</button></div>):(
                  <div><div style={{fontSize:11,color:'#6B7280',marginBottom:5}}>{idUploading?'⏳ Uploading…':'📷 Photo or file of ID'}</div>
                    <input type="file" accept="image/*,application/pdf" style={{display:'none'}} id="qdAdminIdFile" onChange={e=>uploadId(e.target.files[0])}/>
                    <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                      <button type="button" disabled={idUploading} onClick={()=>document.getElementById('qdAdminIdFile')?.click()} style={{fontSize:11,padding:'4px 10px',background:'white',border:'1.5px solid #D1D5DB',borderRadius:6,cursor:'pointer',fontWeight:600}}>📁 Browse</button>
                      <CameraCapture disabled={idUploading} color="#4F46E5" onCapture={f=>uploadId(f)}/>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:6,padding:'8px 10px',fontSize:11,color:'#92400E',lineHeight:1.5}}>⚠️ Admin accounts have <strong>full system access</strong>. Only create for trusted team members.</div>
            <button style={{width:'100%',marginTop:4,padding:'10px',background:'linear-gradient(135deg,#4F46E5,#7C3AED)',color:'white',border:'none',borderRadius:8,fontWeight:800,fontSize:13,cursor:'pointer'}} onClick={submit} disabled={creating}>{creating?'⏳ Creating…':'🏢 Create Admin Account'}</button>
          </div>)}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData]               = useState({totalUsers:0,activePolicies:0,pendingClaims:0,totalClaims:0,totalPayouts:0,totalRevenue:0,riskDistribution:{green:0,yellow:0,red:0},recentClaims:[],claimsByType:{bail:[],funeral:[],income:[]},suspended:0,allUsers:[]});
  const [agents, setAgents]           = useState([]);
  const [pending, setPending]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [tab, setTab]                 = useState('overview');
  const [reviewPerson, setReviewPerson] = useState(null);
  const [suspendPerson, setSuspendPerson] = useState(null);
  const navigate = useNavigate();
  const [showNewAgent,setShowNewAgent]=useState(false);
  const [showNewAdmin,setShowNewAdmin]=useState(false);

  const load = async () => {
    try {
      const [d, a, p] = await Promise.all([
        usersAPI.getAdminDashboard(),
        usersAPI.getAgentReport(),
        usersAPI.getPendingOnboards(),
      ]);
      setData(d.data);
      setAgents(a.data.agents||[]);
      setPending(p.data.pending||[]);
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(()=>{ load(); }, []);

  const handleApprove = async (id) => {
    await usersAPI.approveKyc(id);
    setReviewPerson(null); load();
  };
  const handleReject = async (id, reason) => {
    await usersAPI.rejectKyc(id, reason||'Documents incomplete or not legible');
    setReviewPerson(null); load();
  };
  const handleSuspend = async (user, reason) => {
    if (user.suspended) await usersAPI.unsuspendUser(user.id);
    else await usersAPI.suspendUser(user.id, reason||'Admin action');
    setSuspendPerson(null); setReviewPerson(null); load();
  };

  
  // data defaults to empty object so page always renders


  const riskData = [
    { name:'Green',  value:data.riskDistribution.green,  color:'#00D68F' },
    { name:'Yellow', value:data.riskDistribution.yellow, color:'#F59E0B' },
    { name:'Red',    value:data.riskDistribution.red,    color:'#F43F5E' },
  ].filter(d=>d.value>0);

  const pendingCount = pending.length;
  const now = new Date();
  const hour = now.getHours();
  // eslint-disable-next-line no-unused-vars
  const greeting = hour<12?'Good morning':hour<17?'Good afternoon':'Good evening';

  return (
    <div>
      <div className="page-body" style={{paddingTop:28}}>

        {/* ── Admin Hero Banner ── */}
        <div style={{
          background:'linear-gradient(135deg,#050E1F 0%,#0D1F40 40%,#071A2E 70%,#06201A 100%)',
          borderRadius:20, padding:'28px 32px', marginBottom:24,
          position:'relative', overflow:'hidden',
          border:'1px solid rgba(255,255,255,.06)',
        }}>
          {/* Background SVG grid */}
          <svg style={{position:'absolute',inset:0,opacity:.04,pointerEvents:'none'}} width="100%" height="100%">
            <defs><pattern id="ag" width="36" height="36" patternUnits="userSpaceOnUse"><path d="M 36 0 L 0 0 0 36" fill="none" stroke="white" strokeWidth="1"/></pattern></defs>
            <rect width="100%" height="100%" fill="url(#ag)"/>
          </svg>
          {/* Glow orbs */}
          <div style={{position:'absolute',top:-60,left:200,width:240,height:240,borderRadius:'50%',background:'radial-gradient(circle,rgba(0,214,143,.1) 0%,transparent 70%)',pointerEvents:'none'}}/>
          <div style={{position:'absolute',bottom:-80,right:100,width:300,height:300,borderRadius:'50%',background:'radial-gradient(circle,rgba(79,70,229,.1) 0%,transparent 70%)',pointerEvents:'none'}}/>

          {/* Admin motorbike SVG — right side */}
          <div style={{position:'absolute',right:24,top:'50%',transform:'translateY(-50%)',opacity:.85,pointerEvents:'none'}}>
            <svg width="320" height="180" viewBox="0 0 320 180" fill="none">
              {/* City skyline silhouette */}
              <rect x="0" y="130" width="320" height="50" fill="rgba(255,255,255,.02)"/>
              <rect x="20" y="100" width="18" height="30" fill="rgba(255,255,255,.04)" rx="2"/>
              <rect x="44" y="85" width="14" height="45" fill="rgba(255,255,255,.05)" rx="2"/>
              <rect x="64" y="95" width="20" height="35" fill="rgba(255,255,255,.04)" rx="2"/>
              <rect x="250" y="88" width="16" height="42" fill="rgba(255,255,255,.04)" rx="2"/>
              <rect x="272" y="98" width="22" height="32" fill="rgba(255,255,255,.04)" rx="2"/>
              <rect x="300" y="80" width="14" height="50" fill="rgba(255,255,255,.05)" rx="2"/>
              {/* Road */}
              <rect x="0" y="148" width="320" height="3" fill="rgba(255,255,255,.08)" rx="2"/>
              <rect x="30" y="155" width="40" height="2" fill="rgba(255,255,255,.06)" rx="1"/>
              <rect x="100" y="155" width="40" height="2" fill="rgba(255,255,255,.06)" rx="1"/>
              <rect x="170" y="155" width="40" height="2" fill="rgba(255,255,255,.06)" rx="1"/>
              <rect x="240" y="155" width="40" height="2" fill="rgba(255,255,255,.06)" rx="1"/>

              {/* Motorbike — detailed */}
              <g transform="translate(90,55)">
                {/* Speed lines */}
                <line x1="-30" y1="55" x2="-8" y2="55" stroke="rgba(0,214,143,.6)" strokeWidth="2" strokeLinecap="round"/>
                <line x1="-40" y1="65" x2="-12" y2="65" stroke="rgba(0,214,143,.4)" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="-24" y1="45" x2="-6" y2="45" stroke="rgba(0,214,143,.3)" strokeWidth="1" strokeLinecap="round"/>

                {/* Rear wheel */}
                <circle cx="22" cy="73" r="22" stroke="rgba(255,255,255,.3)" strokeWidth="3.5" fill="none"/>
                <circle cx="22" cy="73" r="14" stroke="rgba(255,255,255,.12)" strokeWidth="2" fill="none"/>
                <circle cx="22" cy="73" r="5" fill="rgba(255,255,255,.35)"/>
                {/* Spokes */}
                {[0,60,120,180,240,300].map(a=>(
                  <line key={a}
                    x1={22+5*Math.cos(a*Math.PI/180)} y1={73+5*Math.sin(a*Math.PI/180)}
                    x2={22+14*Math.cos(a*Math.PI/180)} y2={73+14*Math.sin(a*Math.PI/180)}
                    stroke="rgba(255,255,255,.2)" strokeWidth="1.2"/>
                ))}

                {/* Frame */}
                <path d="M22 73 L44 38 L85 36 L110 73" stroke="rgba(255,255,255,.4)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M44 38 L58 14 L82 36" stroke="rgba(255,255,255,.3)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M85 36 L110 55 L110 73" stroke="rgba(255,255,255,.3)" strokeWidth="2.5" fill="none" strokeLinecap="round"/>

                {/* Engine/body fill */}
                <path d="M50 38 L82 36 L85 52 L50 54 Z" fill="rgba(255,255,255,.08)" stroke="rgba(255,255,255,.2)" strokeWidth="1.5"/>

                {/* Exhaust */}
                <path d="M24 70 Q16 74 10 82" stroke="rgba(255,165,0,.5)" strokeWidth="2" strokeLinecap="round" fill="none"/>
                <path d="M5 82 Q2 78 8 76 Q14 74 10 82" fill="rgba(255,165,0,.2)" stroke="rgba(255,165,0,.4)" strokeWidth="1"/>

                {/* Fuel tank */}
                <path d="M55 30 Q68 24 80 28 L80 38 L54 40 Z" fill="rgba(0,214,143,.25)" stroke="rgba(0,214,143,.5)" strokeWidth="1.5"/>

                {/* Seat */}
                <path d="M58 24 Q68 18 80 22" stroke="rgba(0,214,143,.7)" strokeWidth="3.5" strokeLinecap="round"/>

                {/* Front wheel */}
                <circle cx="110" cy="73" r="22" stroke="rgba(255,255,255,.3)" strokeWidth="3.5" fill="none"/>
                <circle cx="110" cy="73" r="14" stroke="rgba(255,255,255,.12)" strokeWidth="2" fill="none"/>
                <circle cx="110" cy="73" r="5" fill="rgba(255,255,255,.35)"/>
                {[0,60,120,180,240,300].map(a=>(
                  <line key={a}
                    x1={110+5*Math.cos(a*Math.PI/180)} y1={73+5*Math.sin(a*Math.PI/180)}
                    x2={110+14*Math.cos(a*Math.PI/180)} y2={73+14*Math.sin(a*Math.PI/180)}
                    stroke="rgba(255,255,255,.2)" strokeWidth="1.2"/>
                ))}

                {/* Fork */}
                <path d="M82 36 L106 58" stroke="rgba(255,255,255,.3)" strokeWidth="2.5" strokeLinecap="round"/>

                {/* Rider */}
                <circle cx="70" cy="8" r="9" fill="rgba(0,214,143,.35)" stroke="rgba(0,214,143,.7)" strokeWidth="2"/>
                {/* Helmet shine */}
                <path d="M64 4 Q68 2 73 5" stroke="rgba(255,255,255,.4)" strokeWidth="1.5" strokeLinecap="round"/>
                {/* Body */}
                <path d="M70 17 L66 34 M66 34 L58 42 M66 34 L74 40" stroke="rgba(255,255,255,.35)" strokeWidth="2.2" strokeLinecap="round"/>
                {/* Headlight */}
                <circle cx="120" cy="60" r="6" fill="rgba(255,220,80,.6)" stroke="rgba(255,220,80,.8)" strokeWidth="1.5"/>
                <ellipse cx="130" cy="59" rx="8" ry="4" fill="rgba(255,220,80,.15)"/>
              </g>

              {/* Shield floating above */}
              <g transform="translate(250,18)">
                <path d="M28 4 L52 14 L52 34 C52 50 28 60 28 60 C28 60 4 50 4 34 L4 14 Z"
                  fill="rgba(0,214,143,.12)" stroke="rgba(0,214,143,.5)" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M28 12 L44 20 L44 34 C44 46 28 54 28 54 C28 54 12 46 12 34 L12 20 Z"
                  fill="rgba(0,214,143,.06)" stroke="rgba(0,214,143,.3)" strokeWidth="1"/>
                <path d="M18 33 L25 41 L40 24" stroke="#00D68F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                {/* Glow */}
                <circle cx="28" cy="32" r="20" fill="rgba(0,214,143,.04)"/>
              </g>
            </svg>
          </div>

          {/* Content */}
          <div style={{position:'relative',zIndex:2,maxWidth:'58%'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
              <div style={{width:44,height:44,borderRadius:12,background:'linear-gradient(135deg,#00D68F,#007A50)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,boxShadow:'0 0 20px rgba(0,214,143,.4)'}}>
                🏢
              </div>
              <div>
                <div style={{fontSize:10,color:'rgba(255,255,255,.4)',fontWeight:700,letterSpacing:1.2,textTransform:'uppercase'}}>Command Centre</div>
                <h2 style={{fontSize:22,fontWeight:800,color:'white',letterSpacing:'-0.5px',lineHeight:1}}>Admin Overview</h2>
              </div>
            </div>
            <div style={{fontSize:13,color:'rgba(255,255,255,.5)',marginBottom:16}}>
              PikiShield operations &amp; compliance — {now.toLocaleDateString('en-KE',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
            </div>

            {/* Compact key stats inline */}
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              {[
                {label:'Members',   value:data.totalUsers,    color:'#00D68F', icon:'👥'},
                {label:'Policies',  value:data.activePolicies,color:'#A5B4FC', icon:'🛡️'},
                {label:'Pending',   value:data.pendingClaims, color:'#FCD34D', icon:'⏳',
                  alert:data.pendingClaims>0, onClick:()=>navigate('/admin/claims')},
                {label:'KYC Queue', value:pendingCount,        color:'#F87171', icon:'📋',
                  alert:pendingCount>0},
              ].map(s=>(
                <div key={s.label} onClick={s.onClick}
                  style={{
                    background:'rgba(255,255,255,.07)',
                    border:`1px solid ${s.alert?s.color+'55':'rgba(255,255,255,.1)'}`,
                    borderRadius:12,padding:'10px 16px',cursor:s.onClick?'pointer':'default',
                    transition:'background .15s',
                    boxShadow:s.alert?`0 0 12px ${s.color}33`:undefined,
                  }}
                  onMouseEnter={e=>{ if(s.onClick) e.currentTarget.style.background='rgba(255,255,255,.12)'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,.07)'; }}>
                  <div style={{fontSize:9,color:'rgba(255,255,255,.45)',fontWeight:700,letterSpacing:.8,textTransform:'uppercase',marginBottom:3}}>{s.icon} {s.label}</div>
                  <div style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,fontSize:20,color:s.color,lineHeight:1}}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons — top right */}
          <div style={{position:'absolute',top:20,right:20,zIndex:3,display:'flex',gap:8}}>
            <button className="btn btn-sm" style={{background:'rgba(255,255,255,.1)',color:'white',border:'1px solid rgba(255,255,255,.2)',backdropFilter:'blur(8px)'}} onClick={()=>navigate('/admin/claims')}>
              📋 Claims {data.pendingClaims>0&&<span style={{background:'#F43F5E',borderRadius:10,padding:'0 5px',fontSize:10,fontWeight:800,marginLeft:4}}>{data.pendingClaims}</span>}
            </button>
            <button className="btn btn-sm" style={{background:'rgba(255,255,255,.1)',color:'white',border:'1px solid rgba(255,255,255,.2)',backdropFilter:'blur(8px)'}} onClick={()=>navigate('/admin/users')}>
              👥 Members
            </button>
            <button className="btn btn-sm" style={{background:'rgba(5,150,105,.7)',color:'white',border:'1px solid rgba(0,214,143,.3)',backdropFilter:'blur(8px)'}} onClick={()=>setShowNewAgent(true)}>
              🤝 New Agent
            </button>
            <button className="btn btn-sm" style={{background:'rgba(79,70,229,.7)',color:'white',border:'1px solid rgba(165,180,252,.3)',backdropFilter:'blur(8px)'}} onClick={()=>setShowNewAdmin(true)}>
              🏢 New Admin
            </button>
          </div>
        </div>

        {/* ── Tab nav ── */}
        <div style={{display:'flex',gap:6,marginBottom:20,background:'var(--surface)',padding:6,borderRadius:12,border:'1px solid var(--border)',width:'fit-content'}}>
          {[
            ['overview','📊','Overview'],
            ['onboards',`👤`,`Onboarders${pendingCount>0?` · ${pendingCount}`:''}`,],
            ['agents','🤝','Agents'],
          ].map(([k,ic,l])=>(
            <button key={k} onClick={()=>setTab(k)}
              style={{
                padding:'7px 16px',borderRadius:8,border:'none',cursor:'pointer',
                background:tab===k?'linear-gradient(135deg,var(--emerald),var(--emerald-dark))':'transparent',
                color:tab===k?'white':'var(--muted)',
                fontWeight:tab===k?700:500,fontSize:13,fontFamily:'Inter,sans-serif',
                transition:'all .15s',
                boxShadow:tab===k?'0 2px 8px rgba(0,214,143,.3)':undefined,
              }}>
              {ic} {l}
              {k==='onboards' && pendingCount>0 && (
                <span style={{marginLeft:4,background:'#F43F5E',color:'white',borderRadius:10,padding:'1px 6px',fontSize:9,fontWeight:800}}>{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* ─── OVERVIEW ─── */}
        {tab === 'overview' && (
          <>

            {/* Contribution breakdown */}
            {data.contribByType && (
              <div className="card" style={{marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
                  <span style={{width:24,height:24,borderRadius:6,background:'var(--emerald-light)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:13}}>💳</span>
                  Contributions by Policy Type
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                  {[
                    {key:'bail',       label:'Bail Protection',    color:'#4F46E5',bg:'#EEF2FF',icon:'⚖️'},
                    {key:'bail_income',label:'Bail + Stipend',     color:'#00D68F',bg:'#E6FBF3',icon:'🏥'},
                    {key:'funeral',    label:'Funeral Protection', color:'#F59E0B',bg:'#FFFBEB',icon:'🕊️'},
                  ].map(({key,label,color,bg,icon})=>(
                    <div key={key} style={{background:bg,borderRadius:10,padding:'10px 12px',border:`1px solid ${color}22`}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                        <span style={{fontSize:14}}>{icon}</span>
                        <div style={{fontSize:11,fontWeight:600,color:'var(--text-2)'}}>{label}</div>
                      </div>
                      <div style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,fontSize:16,color}}>
                        KES {(data.contribByType[key]||0).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="two-col" style={{marginBottom:16}}>
              {/* Risk distribution donut */}
              <div className="card" style={{padding:'14px'}}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:10,display:'flex',alignItems:'center',gap:6}}>
                  <span style={{width:22,height:22,borderRadius:6,background:'var(--emerald-light)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:12}}>📊</span>
                  Risk Distribution
                </div>
                {riskData.length > 0 ? (
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <ResponsiveContainer width={110} height={110}>
                      <PieChart>
                        <Pie data={riskData} cx="50%" cy="50%" innerRadius={28} outerRadius={50} dataKey="value" paddingAngle={4}>
                          {riskData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                        </Pie>
                        <Tooltip formatter={(v,n)=>[v,n]}/>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {riskData.map(d=>(
                        <div key={d.name} style={{display:'flex',alignItems:'center',gap:6}}>
                          <div style={{width:8,height:8,borderRadius:2,background:d.color,flexShrink:0}}/>
                          <span style={{fontSize:11,color:'var(--muted)'}}>{d.name}</span>
                          <span style={{fontWeight:700,fontSize:12,marginLeft:'auto',color:d.color}}>{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <div style={{height:110,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',fontSize:12}}>No risk data yet</div>}
              </div>

              {/* Recent claims */}
              <div className="card" style={{padding:'14px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                  <div style={{fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:6}}>
                    <span style={{width:22,height:22,borderRadius:6,background:'var(--amber-light)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:12}}>📋</span>
                    Recent Claims
                  </div>
                  <button className="btn btn-sm btn-secondary" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>navigate('/admin/claims')}>All →</button>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:5}}>
                  {(data.recentClaims||[]).slice(0,4).map(c=>(
                    <div key={c.id} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--bg)',borderRadius:8}}>
                      <span style={{fontSize:14}}>{TYPE_META[c.type]?.icon||'📋'}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:11,fontWeight:650,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.userName||'—'}</div>
                        <div style={{fontSize:10,color:'var(--muted)'}}>KES {c.amount?.toLocaleString()} · {c.type}</div>
                      </div>
                      <span className={`status-badge status-${c.status}`} style={{fontSize:9,padding:'2px 7px'}}>{c.status}</span>
                    </div>
                  ))}
                  {(data.recentClaims||[]).length===0 && (
                    <div style={{textAlign:'center',padding:'16px 0',color:'var(--muted)',fontSize:12}}>No claims yet</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ─── PENDING ONBOARDERS ─── */}
        {tab === 'onboards' && (
          <div className="card">
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14 }}>
              <div>
                <div style={{ fontSize:14,fontWeight:700 }}>New Onboarders — KYC Review</div>
                <div style={{ fontSize:12,color:'var(--muted)' }}>Review documents before approving accounts</div>
              </div>
              <span style={{ fontSize:12,background:'#FFF8E1',color:'#92400E',padding:'4px 12px',borderRadius:20,fontWeight:600 }}>
                {pendingCount} pending
              </span>
            </div>
            {pending.length === 0 ? (
              <div className="empty-state"><div className="emoji">✅</div><p>All onboarders reviewed!</p></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Name</th><th>Member No.</th><th>Type</th><th>County</th><th>Docs</th><th>Agent</th><th>Joined</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {pending.map(p=>(
                      <tr key={p.id}>
                        <td style={{ fontWeight:600 }}>{p.fullName}<div style={{ fontSize:11,color:'var(--muted)' }}>{p.phone}</div></td>
                        <td style={{ fontSize:11,fontFamily:'monospace',color:'var(--muted)' }}>{p.memberNumber||'—'}</td>
                        <td><span className={`tier-badge ${p.role==='rider'?'tier-green':'tier-yellow'}`}>{p.role}</span></td>
                        <td style={{ fontSize:12 }}>{p.profile?.county||'—'}</td>
                        <td style={{ fontSize:12 }}>📎 {p.docs?.length||0}</td>
                        <td style={{ fontSize:11,color:'var(--muted)' }}>{p.agentName||'—'}</td>
                        <td style={{ fontSize:11,color:'var(--muted)' }}>{new Date(p.createdAt).toLocaleDateString('en-KE')}</td>
                        <td>
                          <button className="btn btn-sm btn-secondary" onClick={()=>setReviewPerson(p)}>🔍 Review</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── AGENT REPORTS ─── */}
        {tab === 'agents' && (
          <>
            {/* Agent performance bar chart */}
            {agents.length > 0 && (
              <div className="card" style={{ marginBottom:20 }}>
                <div style={{ fontSize:13,fontWeight:700,marginBottom:12 }}>Agent Onboarding Performance</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={agents.map(a=>({ name:a.name.split(' ')[0], riders:a.ridersCount, members:a.membersCount, kyc:a.approvedKYC }))} barSize={18}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                    <XAxis dataKey="name" tick={{fontSize:11}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:10}} axisLine={false} tickLine={false}/>
                    <Tooltip/>
                    <Bar dataKey="riders"  name="Riders"    fill="#22C55E" radius={[4,4,0,0]}/>
                    <Bar dataKey="members" name="Members"   fill="#0EA5E9" radius={[4,4,0,0]}/>
                    <Bar dataKey="kyc"     name="KYC Approved" fill="#6366F1" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="card">
              <div style={{ fontSize:14,fontWeight:700,marginBottom:14 }}>Agent Performance Details</div>
              {agents.length === 0 ? (
                <div className="empty-state"><div className="emoji">🤝</div><p>No agents registered yet</p></div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Agent</th><th>Code</th><th>Region</th>
                        <th>Riders</th><th>Members</th><th>KYC Approved</th><th>KYC Pending</th><th>Claims</th><th>Approval Rate</th><th>Last Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map(a=>(
                        <tr key={a.id}>
                          <td style={{ fontWeight:600 }}>{a.name}<div style={{ fontSize:11,color:'var(--muted)' }}>{a.phone}</div></td>
                          <td style={{ fontFamily:'monospace',fontSize:12,color:'var(--green)',fontWeight:700 }}>{a.agentCode}</td>
                          <td style={{ fontSize:12 }}>{a.region}</td>
                          <td style={{ fontWeight:600,textAlign:'center' }}>{a.ridersCount}</td>
                          <td style={{ fontWeight:600,textAlign:'center' }}>{a.membersCount}</td>
                          <td style={{ fontWeight:600,textAlign:'center',color:'var(--green)' }}>{a.approvedKYC}</td>
                          <td style={{ fontWeight:600,textAlign:'center',color:'var(--yellow)' }}>{a.pendingKYC}</td>
                          <td style={{ textAlign:'center' }}>{a.claimsCount}</td>
                          <td>
                            <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                              <div style={{ flex:1,height:5,background:'var(--border)',borderRadius:3,overflow:'hidden' }}>
                                <div style={{ width:`${a.kycApprovalRate}%`,height:'100%',background:a.kycApprovalRate>=80?'var(--green)':a.kycApprovalRate>=50?'#EAB308':'var(--red)',borderRadius:3 }}/>
                              </div>
                              <span style={{ fontSize:11,fontWeight:600,width:32 }}>{a.kycApprovalRate}%</span>
                            </div>
                          </td>
                          <td style={{ fontSize:11,color:'var(--muted)' }}>{new Date(a.lastActivity).toLocaleDateString('en-KE')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <OnboardReviewModal person={reviewPerson} onClose={()=>setReviewPerson(null)}
        onApprove={handleApprove} onReject={handleReject} onSuspend={setSuspendPerson}/>
      <SuspendModal user={suspendPerson} onClose={()=>setSuspendPerson(null)} onSuspend={handleSuspend}/>
      {showNewAgent&&<QuickCreateAgent onClose={()=>setShowNewAgent(false)} onDone={()=>setShowNewAgent(false)}/>}
      {showNewAdmin&&<QuickCreateAdmin onClose={()=>setShowNewAdmin(false)} onDone={()=>setShowNewAdmin(false)}/>}
    </div>
  );
}

const TYPE_META = {
  bail:    { icon:'🚔' },
  funeral: { icon:'🕊️' },
  income:  { icon:'💊' },
};
